/**
 * Build order API payloads (orders header + order_items).
 * Fulfillment status is only on line items; header uses coupon_code / use_store_credit.
 */

import { getPreferredNotifyChannel } from './notifyChannel';

export function buildOrderCreatePayload({
    orderItems,
    orderSummary,
    couponCode = '',
    useStoreCredit = false,
    deliveryAddressId = null,
    notificationChannel,
}) {
    const payload = {
        order_items: orderItems,
        order_summary: orderSummary,
        coupon_code: couponCode || '',
        use_store_credit: Boolean(useStoreCredit),
    };
    if (deliveryAddressId) {
        payload.deliveryAddressId = deliveryAddressId;
    }
    const channel = notificationChannel || getPreferredNotifyChannel();
    if (channel) {
        payload.notification_channel = channel;
    }
    return payload;
}

export function buildOrderUpdatePayload({
    orderSummary,
    couponCode = '',
    useStoreCredit = false,
    deliveryAddressId = null,
}) {
    const payload = {
        order_summary: orderSummary,
        coupon_code: couponCode || '',
        use_store_credit: Boolean(useStoreCredit),
    };
    if (deliveryAddressId) {
        payload.deliveryAddressId = deliveryAddressId;
    }
    return payload;
}

export function orderUsesStoreCredit(order) {
    return order?.use_store_credit === true;
}

export function orderCouponCode(order) {
    return order?.coupon_code ? String(order.coupon_code) : '';
}

export function orderPlacedAt(order) {
    return order?.createdAt || order?.placedAt || null;
}

/** Map API order_items row to Orders UI shape. */
export function mapOrderItemFromApi(item, apiBaseUrl = '') {
    const productData = item.product_data || {};
    const base = apiBaseUrl.replace(/\/$/, '');
    const toAbs = (url) => {
        if (!url || typeof url !== 'string') return url;
        if (
            url.startsWith('http://') ||
            url.startsWith('https://') ||
            url.startsWith('data:') ||
            url.startsWith('blob:')
        ) {
            return url;
        }
        return `${base}/${String(url).replace(/^\//, '')}`;
    };
    const reviewDataRaw = item.reviewData || null;
    const imageUrls =
        reviewDataRaw?.images && Array.isArray(reviewDataRaw.images)
            ? reviewDataRaw.images.map((url) => toAbs(url))
            : [];
    const reviewData = reviewDataRaw
        ? { rating: reviewDataRaw.rating, comment: reviewDataRaw.comment || '', images: imageUrls }
        : null;
    const itemStatus = item.status || 'Processing';
    const hasReviewed =
        !!item.hasReviewed ||
        !!(reviewDataRaw?.rating != null && String(reviewDataRaw.rating).trim() !== '');
    const shipping = item.shipping || null;

    return {
        orderId: item.order_id,
        orderNumber: item.order_number || '',
        orderItemId: item.product_id,
        title: item.product_title || productData.title || 'Product',
        brand: productData.brand || '',
        price: item.unit_final_price ?? item.unit_price ?? 0,
        mrp: item.unit_mrp ?? 0,
        lineFinalAmount: item.final_amount ?? 0,
        couponShare: item.coupon_discount_amount ?? 0,
        creditShare: item.credit_discount_amount ?? 0,
        productDiscount: item.discount_amount ?? 0,
        selectedQty: item.selectedQty || 1,
        selectedSize: item.selectedSize || '',
        urls: productData.urls
            ? productData.urls.map((url) => toAbs(url)).filter(Boolean)
            : [],
        deliveryStatus: itemStatus,
        deliveryDate: item.delivery?.expected_date || null,
        shipping,
        awb: shipping?.awb || '',
        courierName: shipping?.courier_name || '',
        trackingUrl: shipping?.tracking_url || '',
        createdAt: item.createdAt,
        id:
            item._id?.toString() ||
            `${item.order_id}_${item.product_id}_${item.selectedSize || ''}`,
        itemStatus,
        size: item.selectedSize || 'M',
        qty: item.selectedQty || 1,
        status: itemStatus,
        hasReviewed,
        reviewData,
    };
}
